import React, { useState, useEffect, useRef, useCallback } from 'react'
import { observer } from 'mobx-react-lite'

import { P, H3 } from '@mxenabled/mxui'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

import { Drawer, Loader, useGlobalCopyStore, useGlobalUiStore } from '../../common'
import { ConnectMoreAccountsCard } from './ConnectMoreAccountsCard'

interface KeyIndicatorsDrawerProps {
  additionalContent?: React.ReactNode
  connectTitle?: string
  connectDescription?: string
  children?: React.ReactNode
  description?: string
  drawerTitle?: string
  isOpen: boolean
  loadConnectAccountsCard?: boolean
  onClose: () => void
  stickyComponent?: React.ReactNode
  title?: string | React.ReactNode
  drawerType?: 'connectDrawer' | 'connectionsDrawer'
}

const KeyIndicatorsDrawer: React.FC<React.PropsWithChildren<KeyIndicatorsDrawerProps>> = ({
  additionalContent,
  connectTitle,
  connectDescription,
  children,
  description,
  drawerTitle,
  isOpen,
  loadConnectAccountsCard = true,
  onClose,
  stickyComponent,
  title,
  drawerType,
}: KeyIndicatorsDrawerProps) => {
  const { finstrong: copy } = useGlobalCopyStore()

  const { isCopyLoaded } = useGlobalUiStore()
  const [isScrolled, setIsScrolled] = useState(false)
  const scrollContainerRef = useRef<HTMLElement | null>(null)

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const hasScrolled = scrollContainerRef.current.scrollTop > 0
      setIsScrolled((prev) => (prev !== hasScrolled ? hasScrolled : prev))
    }
  }, [])

  const contentCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Clean up previous listener
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', handleScroll)
      }

      if (node) {
        // Find the scrollable parent
        let parent = node.parentElement
        while (parent) {
          const { overflow, overflowY } = window.getComputedStyle(parent)
          if (
            overflow === 'auto' ||
            overflowY === 'auto' ||
            overflow === 'scroll' ||
            overflowY === 'scroll'
          ) {
            scrollContainerRef.current = parent
            parent.addEventListener('scroll', handleScroll, { passive: true })
            break
          }
          parent = parent.parentElement
        }
      }
    },
    [handleScroll],
  )

  useEffect(() => {
    if (!isOpen) {
      setIsScrolled(false)
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [handleScroll])

  if (!isCopyLoaded) return <Loader />

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={drawerTitle || copy.key_indicators}>
      <Stack
        ref={contentCallbackRef}
        sx={{ backgroundColor: 'background.default', height: '100%' }}
      >
        {(title || description) && (
          <Stack
            sx={{
              backgroundColor: 'background.paper',
              gap: 8,
              p: 24,
              pb: stickyComponent ? 0 : 24,
            }}
          >
            {title && typeof title === 'string' ? <H3>{title}</H3> : title}
            {description && <P sx={{ lineHeight: '24px' }}>{description}</P>}
          </Stack>
        )}

        {stickyComponent && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              '& > *': {
                borderBottom: isScrolled ? '1px solid' : 'none',
                borderColor: isScrolled ? 'divider' : 'transparent',
              },
            }}
          >
            {stickyComponent}
          </Box>
        )}
        {additionalContent && <Box>{additionalContent}</Box>}
        <Stack sx={{ bgcolor: 'background.default', gap: 24, p: 24 }}>
          {children}
          {loadConnectAccountsCard && (
            <ConnectMoreAccountsCard
              description={connectDescription}
              drawerType={drawerType}
              isDashboard={true}
              sx={{ backgroundColor: 'background.paper' }}
              title={connectTitle}
            />
          )}
        </Stack>
      </Stack>
    </Drawer>
  )
}
export default observer(KeyIndicatorsDrawer)
